import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import CloseIcon from "@mui/icons-material/Close";
import { AdminRecordingScheduleDialog } from "../../components/Administration/AdminRecordingScheduleDialog.tsx";
import {
  adminNodeGenerateRtmpUrl,
  adminNodeGetRecordingSchedule,
} from "../../api/adminNodeCameras.ts";
import {
  cloneRecordingScheduleSlots,
  createEmptyRecordingScheduleSlots,
} from "../../lib/recording-schedule.ts";
import { formatScheduleSummary } from "../../lib/recording-schedule-summary.ts";
import { getUserTimeZone } from "../../utils/userTimezoneHeader.js";
import {
  CAMERA_VENDOR_OPTIONS,
  isHikvisionFamilyVendor,
  normalizeCameraVendor,
  suggestedOnvifPortForVendor,
  type CameraVendor,
} from "../../lib/cameraVendor.ts";
import type { RecordingScheduleSlots } from "../../types/recordingSchedule.ts";

export type AdminOmniRecordingType = "continuous" | "scheduled";
export type AdminOmniCameraSource = "internal" | "external";
/** Hardware vendor (`cameras.vendor`). */
export type AdminOmniCameraVendor = CameraVendor;
/** 360° stream protocol (RTSP/RTMP); sent as `type` on create/update. */
export type AdminOmniCam360StreamType = "rtsp" | "rtmp";

export interface AdminOmniCameraFormValues {
  cameraSource: AdminOmniCameraSource;
  cameraName: string;
  /** Hardware vendor: CP Plus, Go Lens, or Hik Vision. */
  cameraVendor: AdminOmniCameraVendor;
  latitude: string;
  longitude: string;
  cam360StreamType: AdminOmniCam360StreamType;
  cam360Rtsp: string;
  ptzRtsp: string;
  recordingType: AdminOmniRecordingType;
  isActive: boolean;
  recordingEnabled: boolean;
  onvifEnabled: boolean;
  onvifHost: string;
  onvifPort: string;
  onvifUsername: string;
  onvifPassword: string;
  ptzZoomMin: string;
  ptzZoomMax: string;
  ptzPanOffsetDeg: string;
  ptzTiltOffsetDeg: string;
  recordingScheduleSlots: RecordingScheduleSlots | null;
  recordingScheduleTimezone: string | null;
}

const DEFAULT_VALUES: AdminOmniCameraFormValues = {
  cameraSource: "internal",
  cameraName: "",
  cameraVendor: "hikvision",
  latitude: "",
  longitude: "",
  cam360StreamType: "rtsp",
  cam360Rtsp: "",
  ptzRtsp: "",
  recordingType: "continuous",
  isActive: true,
  recordingEnabled: true,
  onvifEnabled: false,
  onvifHost: "",
  onvifPort: "80",
  onvifUsername: "",
  onvifPassword: "",
  ptzZoomMin: "1",
  ptzZoomMax: "25",
  ptzPanOffsetDeg: "0",
  ptzTiltOffsetDeg: "0",
  recordingScheduleSlots: null,
  recordingScheduleTimezone: null,
};

function parseRecordingScheduleSlots(raw: unknown): RecordingScheduleSlots | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
  const out = {} as RecordingScheduleSlots;
  for (const day of days) {
    const arr = o[day];
    if (!Array.isArray(arr) || arr.length !== 48) return null;
    out[day] = arr.map((b) => Boolean(b));
  }
  return out;
}

function normalizeCameraSource(raw: unknown): AdminOmniCameraSource {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  return s === "external" ? "external" : "internal";
}

function inferCam360StreamType(url: string): AdminOmniCam360StreamType {
  return /^rtmp:\/\//i.test(url.trim()) ? "rtmp" : "rtsp";
}

function normalizeCam360StreamType(
  raw: unknown,
  urlFallback: string,
): AdminOmniCam360StreamType {
  if (raw === 1 || raw === "1") return "rtmp";
  if (raw === 0 || raw === "0") return "rtsp";
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "rtmp" || s === "1") return "rtmp";
  if (s === "rtsp" || s === "0") return "rtsp";
  return inferCam360StreamType(urlFallback);
}

/** API `type` field for POST/PUT /api/cameras (0 = RTSP, 1 = RTMP). */
export function cam360StreamTypeToApiType(
  streamType: AdminOmniCam360StreamType,
): 0 | 1 {
  return streamType === "rtmp" ? 1 : 0;
}

function normalizeRecordingType(raw: unknown): AdminOmniRecordingType {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "schedule" || s === "scheduled") return "scheduled";
  return "continuous";
}

function readBoolean(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0) return false;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }
  return fallback;
}

/** Map a table row / payload-shaped record into form state (no HTTP). */
export function adminOmniRowRecordToFormValues(
  d: Record<string, unknown>,
): AdminOmniCameraFormValues {
  const onvifHost = String(d.onvif_host ?? d.onvif_ip ?? "").trim();
  const onvifPortRaw = d.onvif_port ?? d.onvif_http_port;
  const onvifPort =
    onvifPortRaw != null && String(onvifPortRaw).trim() !== ""
      ? String(onvifPortRaw).trim()
      : "80";
  const onvifEnabledRaw = d.onvif_enabled ?? d.enable_onvif;
  const onvifEnabled =
    onvifEnabledRaw === true ||
    onvifEnabledRaw === 1 ||
    (typeof onvifEnabledRaw === "string" &&
      onvifEnabledRaw.trim().toLowerCase() === "true") ||
    Boolean(onvifHost);

  const numStr = (v: unknown, fallback: string): string => {
    if (v == null || v === "") return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? String(n) : fallback;
  };

  const cameraSource = normalizeCameraSource(
    d.camera_source ??
      d.cameraSource ??
      (d.has_internal === false || d.hasInternal === false ? "external" : "internal"),
  );

  const latRaw = d.latitude ?? d.lat;
  const lngRaw = d.longitude ?? d.lng ?? d.lon;

  const rtspUrl = String(
    d.cam360_rtsp ??
      d.cam360Rtsp ??
      d.streaming_url ??
      d.stream_url ??
      "",
  ).trim();
  const rtmpUrl = String(d.rtmp_url ?? d.rtmpUrl ?? "").trim();
  const cam360StreamType = normalizeCam360StreamType(
    d.type ?? d.camera_type ?? d.cameraType,
    rtmpUrl || rtspUrl,
  );
  const cam360Url =
    cam360StreamType === "rtmp" ? rtmpUrl || rtspUrl : rtspUrl || rtmpUrl;

  return {
    cameraSource,
    cameraName: String(d.name ?? "").trim(),
    cameraVendor: normalizeCameraVendor(
      d.vendor ?? d.camera_vendor ?? d.cameraVendor,
    ),
    latitude:
      latRaw != null && String(latRaw).trim() !== "" ? String(latRaw).trim() : "",
    longitude:
      lngRaw != null && String(lngRaw).trim() !== ""
        ? String(lngRaw).trim()
        : "",
    cam360Rtsp: cam360Url,
    cam360StreamType,
    ptzRtsp: String(d.ptz_rtsp ?? "").trim(),
    recordingType: normalizeRecordingType(
      d.recording_type ?? d.recording_mode ?? d.recordingType ?? d.recordingMode,
    ),
    isActive: readBoolean(d.is_active ?? d.isActive, true),
    recordingEnabled: readBoolean(
      d.recording_enabled ?? d.recordingEnabled,
      true,
    ),
    onvifEnabled,
    onvifHost,
    onvifPort,
    onvifUsername: String(d.onvif_username ?? d.onvif_user ?? "").trim(),
    onvifPassword: String(d.onvif_password ?? "").trim(),
    ptzZoomMin: numStr(d.ptz_zoom_min ?? d.zoom_min, "1"),
    ptzZoomMax: numStr(d.ptz_zoom_max ?? d.zoom_max, "25"),
    ptzPanOffsetDeg: numStr(
      d.ptz_pan_offset_deg ?? d.pan_offset_deg ?? d.pan_offset,
      "0",
    ),
    ptzTiltOffsetDeg: numStr(
      d.ptz_tilt_offset_deg ?? d.tilt_offset_deg ?? d.tilt_offset,
      "0",
    ),
    recordingScheduleSlots: parseRecordingScheduleSlots(
      d.recording_schedule_slots ?? d.recordingScheduleSlots,
    ),
    recordingScheduleTimezone:
      d.recording_schedule_timezone != null
        ? String(d.recording_schedule_timezone).trim() || null
        : d.recordingScheduleTimezone != null
          ? String(d.recordingScheduleTimezone).trim() || null
          : null,
  };
}

function parseOptionalNumber(s: string): number | undefined {
  const t = s?.trim() ?? "";
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** `undefined` = empty, `null` = invalid number. */
function parseCoordinateField(s: string): number | undefined | null {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function adminOmniLatLngFieldsValid(
  latitude: string,
  longitude: string,
): boolean {
  const lat = parseCoordinateField(latitude);
  const lng = parseCoordinateField(longitude);
  if (lat === null || lng === null) return false;
  if (lat === undefined && lng === undefined) return true;
  if (lat === undefined || lng === undefined) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** Request body for POST /cameras and PUT /cameras/{id} (Omni Watch admin). */
export function buildAdminOmniCameraRequestBody(
  values: AdminOmniCameraFormValues,
): Record<string, unknown> {
  const isExternal = values.cameraSource === "external";
  const cam360 = values.cam360Rtsp.trim();
  const ptz = isExternal ? "" : values.ptzRtsp.trim();
  /**
   * Backend uses `streaming_url` to set `cameras.stream_url` (primary detection stream).
   * Prefer PTZ when configured so detections run on the PTZ stream by default.
   */
  const streaming_url = isExternal ? cam360 : ptz || cam360 || "";

  const name = values.cameraName.trim() || (isExternal ? "External camera" : "Camera");

  const streamType = isExternal ? "rtsp" : values.cam360StreamType;

  const body: Record<string, unknown> = {
    name,
    type: cam360StreamTypeToApiType(streamType),
    vendor: values.cameraVendor,
    is_active: values.isActive,
    recording_type: values.recordingType,
    recording_enabled: values.recordingEnabled,
    streaming_url,
    camera_source: values.cameraSource,
  };

  const lat = parseCoordinateField(values.latitude);
  const lng = parseCoordinateField(values.longitude);
  if (lat !== undefined) body.latitude = lat;
  if (lng !== undefined) body.longitude = lng;

  if (cam360) {
    if (streamType === "rtmp") {
      body.rtmp_url = cam360;
    } else {
      body.cam360_rtsp = cam360;
    }
  }
  if (ptz) body.ptz_rtsp = ptz;

  if (isExternal) {
    return body;
  }

  body.onvif_enabled = Boolean(values.onvifEnabled);
  if (values.onvifEnabled) {
    body.onvif_host = values.onvifHost.trim();
    const port = parseInt(values.onvifPort, 10);
    body.onvif_port = Number.isFinite(port) && port > 0 ? port : 80;
    const u = values.onvifUsername.trim();
    if (u) body.onvif_username = u;
    const p = values.onvifPassword;
    if (p !== "") body.onvif_password = p;
  }

  const zm = parseOptionalNumber(values.ptzZoomMin);
  const zx = parseOptionalNumber(values.ptzZoomMax);
  const po = parseOptionalNumber(values.ptzPanOffsetDeg);
  const to = parseOptionalNumber(values.ptzTiltOffsetDeg);
  if (zm !== undefined) body.ptz_zoom_min = zm;
  if (zx !== undefined) body.ptz_zoom_max = zx;
  if (po !== undefined) body.ptz_pan_offset_deg = po;
  if (to !== undefined) body.ptz_tilt_offset_deg = to;

  return body;
}

const RTSP_INVALID_MSG = "Enter a valid RTSP URL.";
const RTMP_INVALID_MSG = "Enter a valid RTMP URL.";

export type AdminOmniCameraFieldErrors = Partial<{
  cameraName: string;
  cam360Rtsp: string;
  ptzRtsp: string;
  form: string;
}>;

export type AdminOmniCameraSubmitResult =
  | { ok: true }
  | { ok: false; message: string; fieldErrors?: AdminOmniCameraFieldErrors };

function isRtspUrl(url: string): boolean {
  const t = url.trim();
  return t.length === 0 || /^rtsp:\/\/.+/i.test(t);
}

function isRtmpUrl(url: string): boolean {
  const t = url.trim();
  return t.length === 0 || /^rtmp:\/\/.+/i.test(t);
}

function isCam360StreamUrl(
  url: string,
  streamType: AdminOmniCam360StreamType,
): boolean {
  return streamType === "rtmp" ? isRtmpUrl(url) : isRtspUrl(url);
}

/** Client-side checks aligned with Node `CameraService` RTSP validation. */
export function validateAdminOmniCameraForm(
  values: AdminOmniCameraFormValues,
): AdminOmniCameraFieldErrors {
  const errors: AdminOmniCameraFieldErrors = {};
  if (!values.cameraName.trim()) {
    errors.cameraName = "Camera name is required.";
  }

  const isExternal = values.cameraSource === "external";
  const cam360 = values.cam360Rtsp.trim();
  const ptz = values.ptzRtsp.trim();

  const cam360InvalidMsg =
    values.cam360StreamType === "rtmp" ? RTMP_INVALID_MSG : RTSP_INVALID_MSG;

  if (isExternal) {
    if (!cam360) {
      errors.cam360Rtsp = "RTSP URL is required.";
    } else if (!isRtspUrl(cam360)) {
      errors.cam360Rtsp = RTSP_INVALID_MSG;
    }
  } else if (!cam360 && !ptz) {
    errors.cam360Rtsp = "Enter at least one of 360° or PTZ RTSP URL.";
    errors.ptzRtsp = "Enter at least one of 360° or PTZ RTSP URL.";
  } else {
    if (cam360 && !isCam360StreamUrl(cam360, values.cam360StreamType)) {
      errors.cam360Rtsp = cam360InvalidMsg;
    }
    if (ptz && !isRtspUrl(ptz)) {
      errors.ptzRtsp = RTSP_INVALID_MSG;
    }
  }

  return errors;
}

/** Map Node API validation messages to form fields (user-friendly labels). */
export function mapAdminCameraApiErrorToFields(
  message: string,
): AdminOmniCameraFieldErrors {
  const m = message.toLowerCase();
  if (
    m.includes("cam360_rtsp") ||
    m.includes("streaming_url") ||
    m.includes("rtmp_url")
  ) {
    return { cam360Rtsp: RTSP_INVALID_MSG };
  }
  if (m.includes("ptz_rtsp")) {
    return { ptzRtsp: RTSP_INVALID_MSG };
  }
  if (m.includes("at least one")) {
    return {
      cam360Rtsp: "Provide at least one stream URL (360° or PTZ).",
      ptzRtsp: "Provide at least one stream URL (360° or PTZ).",
    };
  }
  if (m.includes("name")) {
    return { cameraName: message };
  }
  return { form: message };
}

export interface AdminOmniWatchAddCameraDialogProps {
  open: boolean;
  onClose: () => void;
  /** When non-null, dialog opens in edit mode with these values (no network). */
  initialValues?: AdminOmniCameraFormValues | null;
  /** Existing camera id — used to load weekly schedule from the Node API in edit mode. */
  editCameraId?: string | null;
  onSubmit: (
    values: AdminOmniCameraFormValues,
  ) => AdminOmniCameraSubmitResult | Promise<AdminOmniCameraSubmitResult>;
}

export function AdminOmniWatchAddCameraDialog({
  open,
  onClose,
  initialValues,
  editCameraId,
  onSubmit,
}: AdminOmniWatchAddCameraDialogProps) {
  const [values, setValues] =
    useState<AdminOmniCameraFormValues>(DEFAULT_VALUES);
  const [fieldErrors, setFieldErrors] = useState<AdminOmniCameraFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleLoadError, setScheduleLoadError] = useState<string | null>(null);
  const [generatingRtmpUrl, setGeneratingRtmpUrl] = useState(false);
  const [rtmpGenerateError, setRtmpGenerateError] = useState<string | null>(null);
  const scheduleFetchRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) {
      setFieldErrors({});
      setValues({ ...DEFAULT_VALUES });
      setScheduleDialogOpen(false);
      setScheduleLoadError(null);
      setGeneratingRtmpUrl(false);
      setRtmpGenerateError(null);
      scheduleFetchRef.current?.abort();
      return;
    }
    if (initialValues) {
      setValues({ ...DEFAULT_VALUES, ...initialValues });
    } else {
      setValues({ ...DEFAULT_VALUES });
    }
    setFieldErrors({});
  }, [open, initialValues]);

  useEffect(() => {
    if (!open || !editCameraId) return;
    const mode = initialValues?.recordingType ?? "continuous";
    if (mode !== "scheduled") return;

    scheduleFetchRef.current?.abort();
    const ac = new AbortController();
    scheduleFetchRef.current = ac;
    setScheduleLoading(true);
    setScheduleLoadError(null);

    adminNodeGetRecordingSchedule(editCameraId, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return;
        setValues((p) => ({
          ...p,
          recordingScheduleSlots: res.slots
            ? cloneRecordingScheduleSlots(res.slots)
            : createEmptyRecordingScheduleSlots(),
          recordingScheduleTimezone: res.timezone || getUserTimeZone(),
        }));
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        const msg = err instanceof Error ? err.message : "Could not load schedule";
        setScheduleLoadError(msg);
        setValues((p) => ({
          ...p,
          recordingScheduleSlots: createEmptyRecordingScheduleSlots(),
          recordingScheduleTimezone: getUserTimeZone(),
        }));
      })
      .finally(() => {
        if (!ac.signal.aborted) setScheduleLoading(false);
      });

    return () => ac.abort();
  }, [open, editCameraId, initialValues?.recordingType]);

  const isExternal = values.cameraSource === "external";
  const scheduleRequired =
    values.recordingEnabled && values.recordingType === "scheduled";
  const scheduleConfigured = values.recordingScheduleSlots !== null;

  const hasStream =
    Boolean(values.cam360Rtsp.trim()) || Boolean(values.ptzRtsp.trim());
  const positionValid = adminOmniLatLngFieldsValid(
    values.latitude,
    values.longitude,
  );

  const canSave = isExternal
    ? Boolean(values.cameraName.trim()) &&
      Boolean(values.cam360Rtsp.trim()) &&
      positionValid
    : Boolean(values.cameraName.trim()) &&
      hasStream &&
      positionValid &&
      (!values.onvifEnabled || Boolean(values.onvifHost.trim())) &&
      (!scheduleRequired || scheduleConfigured);

  const sourceTabIndex = values.cameraSource === "external" ? 1 : 0;

  const handleSave = async () => {
    if (!canSave) return;
    const clientErrors = validateAdminOmniCameraForm(values);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const result = await onSubmit(values);
      if (!result.ok) {
        setFieldErrors(
          result.fieldErrors ?? mapAdminCameraApiErrorToFields(result.message),
        );
        return;
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const formDisabled = false;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden" },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 20,
            fontWeight: 600,
            color: "#E85D2C",
          }}
        >
          {initialValues ? "Edit" : "Add Camera"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ color: "#6B7280", mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, pb: 1, px: 0 }}>
        <Tabs
          value={sourceTabIndex}
          onChange={(_e, index) =>
            setValues((p) => ({
              ...p,
              cameraSource: index === 1 ? "external" : "internal",
            }))
          }
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #E5E7EB",
            minHeight: 44,
            px: 2.5,
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              color: "#6B7280",
            },
            "& .MuiTab-root.Mui-selected": { color: "#2932E5" },
            "& .MuiTabs-indicator": { backgroundColor: "#2932E5", height: 2 },
          }}
        >
          <Tab label="INTERNAL" />
          <Tab label="EXTERNAL" />
        </Tabs>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2,
            px: 2.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#4B5563",
              lineHeight: 1.5,
            }}
          >
            Save sends this camera to the Node API (`/api/cameras`). Continuous
            recording starts when the camera is active, recording is enabled, and
            mode is continuous.
          </Typography>

          {fieldErrors.form ? (
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 13,
                color: "#B91C1C",
                lineHeight: 1.5,
              }}
            >
              {fieldErrors.form}
            </Typography>
          ) : null}

          {isExternal ? (
            <>
              <Field
                label="Camera name"
                placeholder="Camera name"
                helperText={
                  fieldErrors.cameraName ?? "Shown in the camera list after save."
                }
                error={Boolean(fieldErrors.cameraName)}
                value={values.cameraName}
                disabled={formDisabled}
                onChange={(v) => {
                  setFieldErrors((e) => ({ ...e, cameraName: undefined }));
                  setValues((p) => ({ ...p, cameraName: v }));
                }}
              />
              <PositionFields
                latitude={values.latitude}
                longitude={values.longitude}
                disabled={formDisabled}
                onLatitudeChange={(v) =>
                  setValues((p) => ({ ...p, latitude: v }))
                }
                onLongitudeChange={(v) =>
                  setValues((p) => ({ ...p, longitude: v }))
                }
              />
              <Field
                label="RTSP URL"
                placeholder="rtsp://user:pass@host:554/stream"
                helperText={
                  fieldErrors.cam360Rtsp ??
                  "Sent as cam360_rtsp / streaming_url."
                }
                error={Boolean(fieldErrors.cam360Rtsp)}
                value={values.cam360Rtsp}
                disabled={formDisabled}
                onChange={(v) => {
                  setFieldErrors((e) => ({ ...e, cam360Rtsp: undefined }));
                  setValues((p) => ({ ...p, cam360Rtsp: v }));
                }}
              />
            </>
          ) : (
            <>
              <Field
                label="Camera name"
                placeholder="Camera name"
                helperText={
                  fieldErrors.cameraName ?? "Shown in the camera list after save."
                }
                error={Boolean(fieldErrors.cameraName)}
                value={values.cameraName}
                disabled={formDisabled}
                onChange={(v) => {
                  setFieldErrors((e) => ({ ...e, cameraName: undefined }));
                  setValues((p) => ({ ...p, cameraName: v }));
                }}
              />
              <PositionFields
                latitude={values.latitude}
                longitude={values.longitude}
                disabled={formDisabled}
                onLatitudeChange={(v) =>
                  setValues((p) => ({ ...p, latitude: v }))
                }
                onLongitudeChange={(v) =>
                  setValues((p) => ({ ...p, longitude: v }))
                }
              />

              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="admin-omni-camera-vendor-label">Camera type</InputLabel>
                <Select
                  labelId="admin-omni-camera-vendor-label"
                  label="Camera type"
                  variant="outlined"
                  value={values.cameraVendor}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const next = e.target.value as AdminOmniCameraVendor;
                    setValues((p) => {
                      const nextPort = suggestedOnvifPortForVendor(next);
                      const shouldSuggestPort =
                        !p.onvifPort.trim() ||
                        p.onvifPort === "8091" ||
                        p.onvifPort === suggestedOnvifPortForVendor(p.cameraVendor);
                      return {
                        ...p,
                        cameraVendor: next,
                        ...(shouldSuggestPort ? { onvifPort: nextPort } : {}),
                      };
                    });
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 1,
                        border: "1px solid #E5E7EB",
                        mt: 0.5,
                        "& .MuiMenuItem-root.Mui-selected": {
                          bgcolor: "#2932E5",
                          color: "#fff",
                        },
                        "& .MuiMenuItem-root.Mui-selected:hover": {
                          bgcolor: "#1e28b8",
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: "#F3F4F6",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                      borderWidth: 2,
                    },
                  }}
                >
                  {CAMERA_VENDOR_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ fontFamily: "Roboto, sans-serif", fontSize: 12, mx: 0 }}>
                  Camera hardware type (CP Plus, Go Lens, or Hik Vision).
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="admin-omni-cam360-type-label">Stream type</InputLabel>
                <Select
                  labelId="admin-omni-cam360-type-label"
                  label="Stream type"
                  variant="outlined"
                  value={values.cam360StreamType}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const next = e.target.value as AdminOmniCam360StreamType;
                    setRtmpGenerateError(null);
                    setValues((p) => ({ ...p, cam360StreamType: next }));
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 1,
                        border: "1px solid #E5E7EB",
                        mt: 0.5,
                        "& .MuiMenuItem-root.Mui-selected": {
                          bgcolor: "#2932E5",
                          color: "#fff",
                        },
                        "& .MuiMenuItem-root.Mui-selected:hover": {
                          bgcolor: "#1e28b8",
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: "#F3F4F6",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2932E5",
                      borderWidth: 2,
                    },
                  }}
                >
                  <MenuItem value="rtsp">RTSP</MenuItem>
                  <MenuItem value="rtmp">RTMP</MenuItem>
                </Select>
                <FormHelperText sx={{ fontFamily: "Roboto, sans-serif", fontSize: 12, mx: 0 }}>
                  Sent as type (0 = RTSP, 1 = RTMP).
                </FormHelperText>
              </FormControl>

              <Cam360StreamUrlField
                label="360° camera RTSP URL"
                placeholder={
                  values.cam360StreamType === "rtmp"
                    ? "rtmp://host:1935/stream-id"
                    : "rtsp://user:pass@host:554/stream"
                }
                helperText={
                  fieldErrors.cam360Rtsp ??
                  rtmpGenerateError ??
                  (values.cam360StreamType === "rtmp"
                    ? "Sent as rtmp_url."
                    : "Sent as cam360_rtsp.")
                }
                error={Boolean(fieldErrors.cam360Rtsp) || Boolean(rtmpGenerateError)}
                value={values.cam360Rtsp}
                disabled={formDisabled}
                showGenerateUrl={values.cam360StreamType === "rtmp"}
                generatingUrl={generatingRtmpUrl}
                onGenerateUrl={async () => {
                  setRtmpGenerateError(null);
                  setGeneratingRtmpUrl(true);
                  try {
                    const res = await adminNodeGenerateRtmpUrl();
                    setFieldErrors((e) => ({ ...e, cam360Rtsp: undefined }));
                    setValues((p) => ({ ...p, cam360Rtsp: res.rtmpUrl }));
                  } catch (err: unknown) {
                    const msg =
                      err instanceof Error
                        ? err.message
                        : "Failed to generate RTMP URL";
                    setRtmpGenerateError(msg);
                  } finally {
                    setGeneratingRtmpUrl(false);
                  }
                }}
                onChange={(v) => {
                  setFieldErrors((e) => ({ ...e, cam360Rtsp: undefined }));
                  setRtmpGenerateError(null);
                  setValues((p) => ({ ...p, cam360Rtsp: v }));
                }}
              />

              <Field
                label="PTZ camera RTSP URL"
                placeholder="rtsp://user:pass@host:554/stream"
                helperText={fieldErrors.ptzRtsp ?? "Sent as ptz_rtsp."}
                error={Boolean(fieldErrors.ptzRtsp)}
                value={values.ptzRtsp}
                disabled={formDisabled}
                onChange={(v) => {
                  setFieldErrors((e) => ({ ...e, ptzRtsp: undefined }));
                  setValues((p) => ({ ...p, ptzRtsp: v }));
                }}
              />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.isActive}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setValues((p) => ({ ...p, isActive: e.target.checked }))
                  }
                  sx={{
                    color: "#2932E5",
                    "&.Mui-checked": { color: "#2932E5" },
                  }}
                />
              }
              label="Camera active"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.recordingEnabled}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setValues((p) => ({
                      ...p,
                      recordingEnabled: e.target.checked,
                    }))
                  }
                  sx={{
                    color: "#2932E5",
                    "&.Mui-checked": { color: "#2932E5" },
                  }}
                />
              }
              label="Recording enabled"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                },
              }}
            />
          </Box>

          {values.recordingEnabled ? (
          <>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="admin-omni-recording-type-label">
              Recording type
            </InputLabel>
            <Select
              labelId="admin-omni-recording-type-label"
              label="Recording type"
              variant="outlined"
              value={values.recordingType}
              disabled={formDisabled}
              onChange={(e) => {
                const next = e.target.value as AdminOmniRecordingType;
                setValues((p) => ({ ...p, recordingType: next }));
                if (next === "scheduled" && values.recordingEnabled) {
                  setScheduleDialogOpen(true);
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 1,
                    border: "1px solid #E5E7EB",
                    mt: 0.5,
                    "& .MuiMenuItem-root.Mui-selected": {
                      bgcolor: "#2932E5",
                      color: "#fff",
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover": {
                      bgcolor: "#1e28b8",
                    },
                  },
                },
              }}
              sx={{
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  bgcolor: "#F3F4F6",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                  borderWidth: 2,
                },
              }}
            >
              <MenuItem value="continuous">Continuous</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
            </Select>
            <FormHelperText sx={{ fontFamily: "Roboto, sans-serif", fontSize: 12, mx: 0 }}>
              Choose Scheduled to open the weekly grid. Manual start/stop uses separate
              API actions.
            </FormHelperText>
          </FormControl>

          {values.recordingType === "scheduled" ? (
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1,
                bgcolor: "#F9FAFB",
                px: 1.5,
                py: 1.25,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Weekly schedule
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 12,
                  color: scheduleConfigured ? "#374151" : "#B45309",
                }}
              >
                {scheduleLoading
                  ? "Loading schedule…"
                  : formatScheduleSummary(
                      values.recordingScheduleSlots,
                      values.recordingScheduleTimezone,
                    )}
              </Typography>
              {scheduleLoadError ? (
                <Typography sx={{ fontSize: 12, color: "#B91C1C" }}>
                  {scheduleLoadError}
                </Typography>
              ) : null}
              <Button
                size="small"
                variant="outlined"
                disabled={formDisabled || scheduleLoading}
                onClick={() => setScheduleDialogOpen(true)}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontFamily: "Roboto, sans-serif",
                  borderColor: "#2932E5",
                  color: "#2932E5",
                  "&:hover": { borderColor: "#1e28b8", bgcolor: "rgba(41,50,229,0.06)" },
                }}
              >
                {scheduleConfigured ? "Edit schedule" : "Configure schedule"}
              </Button>
            </Box>
          ) : null}
          </>
          ) : null}

          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#212121",
                mb: 1,
              }}
            >
              ONVIF (PTZ control)
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.onvifEnabled}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setValues((p) => ({
                      ...p,
                      onvifEnabled: e.target.checked,
                    }))
                  }
                  sx={{
                    color: "#2932E5",
                    "&.Mui-checked": { color: "#2932E5" },
                  }}
                />
              }
              label="Enable ONVIF"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                },
              }}
            />
            {values.onvifEnabled ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 1,
                  pl: 0.5,
                }}
              >
                <Field
                  label="Camera IP or hostname"
                  placeholder="Camera IP or hostname"
                  value={values.onvifHost}
                  disabled={formDisabled}
                  onChange={(v) => setValues((p) => ({ ...p, onvifHost: v }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="ONVIF HTTP port"
                  type="number"
                  value={values.onvifPort}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setValues((p) => ({ ...p, onvifPort: e.target.value }))
                  }
                  helperText={
                    isHikvisionFamilyVendor(values.cameraVendor)
                      ? values.cameraVendor === "hik_vision"
                        ? "Hik Vision ONVIF is usually port 80 (not 8091). Use the ONVIF user from the camera web UI."
                        : "CP Plus uses the same ONVIF absolute PTZ stack as Hik Vision."
                      : undefined
                  }
                  FormHelperTextProps={{
                    sx: { fontFamily: "Roboto, sans-serif", fontSize: 12, mx: 0 },
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
                <Field
                  label="Username"
                  placeholder="Username"
                  value={values.onvifUsername}
                  disabled={formDisabled}
                  onChange={(v) =>
                    setValues((p) => ({ ...p, onvifUsername: v }))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Password"
                  type="password"
                  placeholder="Password"
                  value={values.onvifPassword}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setValues((p) => ({ ...p, onvifPassword: e.target.value }))
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </Box>
            ) : null}
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#212121",
                mb: 0.5,
              }}
            >
              PTZ calibration (optional)
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 13,
                color: "#6B7280",
                mb: 1.5,
                lineHeight: 1.45,
              }}
            >
              Override the zoom range and panorama→PTZ offsets for this camera.
              Leave any field blank to inherit the built-in defaults (1×–25×
              zoom, 0° pan / tilt offset).
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <TextField
                size="small"
                label="Zoom min (×)"
                type="number"
                value={values.ptzZoomMin}
                disabled={formDisabled}
                onChange={(e) =>
                  setValues((p) => ({ ...p, ptzZoomMin: e.target.value }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <TextField
                size="small"
                label="Zoom max (×)"
                type="number"
                value={values.ptzZoomMax}
                disabled={formDisabled}
                onChange={(e) =>
                  setValues((p) => ({ ...p, ptzZoomMax: e.target.value }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <TextField
                size="small"
                label="Pan offset (°)"
                type="number"
                value={values.ptzPanOffsetDeg}
                disabled={formDisabled}
                onChange={(e) =>
                  setValues((p) => ({
                    ...p,
                    ptzPanOffsetDeg: e.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <TextField
                size="small"
                label="Tilt offset (°)"
                type="number"
                value={values.ptzTiltOffsetDeg}
                disabled={formDisabled}
                onChange={(e) =>
                  setValues((p) => ({
                    ...p,
                    ptzTiltOffsetDeg: e.target.value,
                  }))
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
            </Box>
          </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: "flex-end",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            borderColor: "#2932E5",
            color: "#2932E5",
            "&:hover": {
              borderColor: "#1e28b8",
              bgcolor: "rgba(41,50,229,0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={submitting || !canSave}
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            bgcolor: "#2932E5",
            boxShadow: "none",
            px: 2.5,
            "&:hover": { bgcolor: "#1e28b8", boxShadow: "none" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

    <AdminRecordingScheduleDialog
      open={scheduleDialogOpen}
      onClose={() => setScheduleDialogOpen(false)}
      initialSlots={values.recordingScheduleSlots}
      initialTimezone={values.recordingScheduleTimezone}
      loading={scheduleLoading}
      loadError={scheduleLoadError}
      onSave={(slots, timezone) => {
        setValues((p) => ({
          ...p,
          recordingScheduleSlots: cloneRecordingScheduleSlots(slots),
          recordingScheduleTimezone: timezone,
        }));
        setScheduleLoadError(null);
      }}
    />
    </>
  );
}

function Field({
  label,
  placeholder,
  helperText,
  error,
  value,
  disabled,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  helperText?: string;
  error?: boolean;
}) {
  return (
    <Box>
      <Typography
        component="label"
        sx={{
          display: "block",
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 500,
          color: "#212121",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        helperText={helperText}
        error={error}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        FormHelperTextProps={{
          sx: {
            fontFamily: "Roboto, sans-serif",
            fontSize: 12,
            mx: 0,
            color: error ? "#B91C1C" : undefined,
          },
        }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
      />
    </Box>
  );
}

function Cam360StreamUrlField({
  label,
  placeholder,
  helperText,
  error,
  value,
  disabled,
  showGenerateUrl,
  generatingUrl,
  onGenerateUrl,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  disabled: boolean;
  showGenerateUrl: boolean;
  generatingUrl: boolean;
  onGenerateUrl: () => void | Promise<void>;
  onChange: (v: string) => void;
  helperText?: string;
  error?: boolean;
}) {
  return (
    <Box>
      <Typography
        component="label"
        sx={{
          display: "block",
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 500,
          color: "#212121",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          error={error}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
        />
        {showGenerateUrl ? (
          <Button
            variant="outlined"
            size="small"
            disabled={disabled || generatingUrl}
            onClick={() => {
              void onGenerateUrl();
            }}
            sx={{
              flexShrink: 0,
              mt: 0.25,
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              whiteSpace: "nowrap",
              borderColor: "#2932E5",
              color: "#2932E5",
              "&:hover": {
                borderColor: "#1e28b8",
                bgcolor: "rgba(41,50,229,0.06)",
              },
            }}
          >
            {generatingUrl ? "Generating…" : "Generate url"}
          </Button>
        ) : null}
      </Box>
      {helperText ? (
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 12,
            mt: 0.5,
            color: error ? "#B91C1C" : "#6B7280",
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

function PositionFields({
  latitude,
  longitude,
  disabled,
  onLatitudeChange,
  onLongitudeChange,
}: {
  latitude: string;
  longitude: string;
  disabled: boolean;
  onLatitudeChange: (v: string) => void;
  onLongitudeChange: (v: string) => void;
}) {
  const lat = parseCoordinateField(latitude);
  const lng = parseCoordinateField(longitude);
  const latError =
    lat === null
      ? "Enter a valid latitude (−90 to 90)."
      : lat === undefined && lng !== undefined
        ? "Enter latitude when longitude is set."
        : undefined;
  const lngError =
    lng === null
      ? "Enter a valid longitude (−180 to 180)."
      : lng === undefined && lat !== undefined
        ? "Enter longitude when latitude is set."
        : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 600,
          color: "#111827",
        }}
      >
        Position
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        <Field
          label="Latitude"
          placeholder="e.g. 19.0760"
          helperText={latError ?? "Decimal degrees (WGS84)."}
          error={Boolean(latError)}
          value={latitude}
          disabled={disabled}
          onChange={onLatitudeChange}
        />
        <Field
          label="Longitude"
          placeholder="e.g. 72.8777"
          helperText={lngError ?? "Decimal degrees (WGS84)."}
          error={Boolean(lngError)}
          value={longitude}
          disabled={disabled}
          onChange={onLongitudeChange}
        />
      </Box>
    </Box>
  );
}
